/**
 * Debug presets for each scenario.
 *
 * Each entry maps scenario ID → a complete slotAssignments object that
 * satisfies the scenario constraints and passes validation.
 *
 * Slot ID order matches generatedSlots() in ActivityView:
 *   pipe-1 (horizontal) → valve → pipe-2 (horizontal) → pump →
 *   pipe-3 (diagonal) → fitting-1, fitting-2, fitting-3 (top)
 *
 * Component shapes mirror what the catalog emits on confirm:
 *   pipe    → { ...catalogPipe, category, selectedSize, length_ft, length_m, price }
 *   fitting → { ...catalogFitting, category, material }
 *   valve   → { ...catalogValve, category }
 *   pump    → { ...catalogPump, category }
 */

import catalog from "@/data/catalog.json";

// ─── Catalog lookups ────────────────────────────────────────────────────────

const pipeById = Object.fromEntries(catalog.pipes.map((p) => [p.id, p]));
const fittingById = Object.fromEntries(catalog.fittings.map((f) => [f.id, f]));
const valveById = Object.fromEntries(catalog.valves.map((v) => [v.id, v]));
const pumpById = Object.fromEntries(catalog.pumps.map((p) => [p.id, p]));

// ─── Helper builders (mirror what ComponentCatalog emits on confirm) ────────

function makePipe(pipeId, nominal, length_ft) {
  const pipe = pipeById[pipeId];
  const selectedSize = pipe.sizes.find((s) => s.nominal === nominal);
  return {
    ...pipe,
    category: "pipe",
    selectedSize,
    length_ft,
    length_m: length_ft * 0.3048,
    price: selectedSize.pricePerFoot * length_ft
  };
}

function makeFitting(fittingId, material) {
  return { ...fittingById[fittingId], category: "fitting", material };
}

function makeValve(valveId) {
  return { ...valveById[valveId], category: "valve" };
}

function makePump(pumpId) {
  return { ...pumpById[pumpId], category: "pump" };
}

const BALL_VALVE = makeValve("valve-ball");
const GATE_VALVE = makeValve("valve-gate");

const COUPLING = (mat) => makeFitting("fit-coupling", mat);
const ELBOW_90 = (mat) => makeFitting("fit-elbow-90", mat);
const ELBOW_45 = (mat) => makeFitting("fit-elbow-45", mat);

// ─── Presets ───────────────────────────────────────────────────────────────

export const debugPresets = {
  // Scenario 1: Water Distribution — 50 GPM, 15 m elevation, 30 m pipe, $1500
  // Centrifugal 3 HP + 1.5" PVC pipes + ball valve + couplings
  1: {
    "pipe-1": makePipe("pipe-pvc", 1.5, 32.8),
    valve: BALL_VALVE,
    "pipe-2": makePipe("pipe-pvc", 1.5, 10),
    pump: makePump("pump-centrifugal-md"),
    "pipe-3": makePipe("pipe-pvc", 1.5, 55.6),
    "fitting-1": COUPLING("PVC"),
    "fitting-2": COUPLING("PVC"),
    "fitting-3": ELBOW_90("PVC")
  },

  // Scenario 2: Chemical Transfer — 20 GPM, 12 m elevation, 15 m pipe, $2000
  // Compatible: PVC, Stainless Steel only (sulfuric acid)
  // Centrifugal 3 HP + PVC pipes + ball valve + PVC couplings
  2: {
    "pipe-1": makePipe("pipe-pvc", 1, 16.4),
    valve: BALL_VALVE,
    "pipe-2": makePipe("pipe-pvc", 1, 10),
    pump: makePump("pump-centrifugal-md"),
    "pipe-3": makePipe("pipe-pvc", 1, 22.8),
    "fitting-1": COUPLING("PVC"),
    "fitting-2": COUPLING("PVC"),
    "fitting-3": ELBOW_90("PVC")
  },

  // Scenario 3: HVAC Chilled Water — 60 GPM, 10 m elevation, 60 m pipe, $2500
  // Ethylene glycol — all materials OK
  // Centrifugal 3 HP + 2" PVC + gate valve + couplings
  3: {
    "pipe-1": makePipe("pipe-pvc", 2, 65.6),
    valve: GATE_VALVE,
    "pipe-2": makePipe("pipe-pvc", 2, 10),
    pump: makePump("pump-centrifugal-md"),
    "pipe-3": makePipe("pipe-pvc", 2, 120.4),
    "fitting-1": COUPLING("PVC"),
    "fitting-2": COUPLING("PVC"),
    "fitting-3": ELBOW_45("PVC")
  },

  // Scenario 4: Dairy Processing — 30 GPM, 6 m elevation, 10 m pipe, $2500
  // Milk — Stainless Steel ONLY
  // Centrifugal 1 HP + SS pipes + ball valve + SS couplings
  4: {
    "pipe-1": makePipe("pipe-ss", 1, 10.9),
    valve: BALL_VALVE,
    "pipe-2": makePipe("pipe-ss", 1, 10),
    pump: makePump("pump-centrifugal-sm"),
    "pipe-3": makePipe("pipe-ss", 1, 11.9),
    "fitting-1": COUPLING("Stainless Steel"),
    "fitting-2": COUPLING("Stainless Steel"),
    "fitting-3": ELBOW_90("Stainless Steel")
  },

  // Scenario 5: Seawater Intake — 70 GPM, 10 m elevation, 40 m pipe, $3000
  // Seawater — PVC or Stainless Steel
  // Centrifugal 5 HP + 3" PVC + ball valve + PVC couplings
  5: {
    "pipe-1": makePipe("pipe-pvc", 3, 43.7),
    valve: BALL_VALVE,
    "pipe-2": makePipe("pipe-pvc", 3, 10),
    pump: makePump("pump-centrifugal-lg"),
    "pipe-3": makePipe("pipe-pvc", 3, 77.5),
    "fitting-1": COUPLING("PVC"),
    "fitting-2": COUPLING("PVC"),
    "fitting-3": ELBOW_90("PVC")
  }
};
